import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { STATUSES, fetchAllEntries, normalizeStatus } from '../api/contentApi';
import { formatDate, typeInfo } from '../content/contentTypes';
import { applyPreviewSeo } from '../content/seo';
import StatusBadge from '../components/StatusBadge';
import StatusMessage from '../components/StatusMessage';
import LoadError from '../components/LoadError';
import { SITE_NAME } from '../siteConfig';
import { usePreviewAuth } from '../preview/PreviewGuard';
import useAsync from '../useAsync';

function previewLink(slug) {
  return `${window.location.origin}/preview/${slug}`;
}

function CopyLink({ slug }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    const url = previewLink(slug);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copy this link:', url); // clipboard is unavailable on plain http addresses
    }
  }
  return (
    <button type="button" className="link-button" onClick={copy}>
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

export default function PreviewList() {
  const { lock } = usePreviewAuth();
  const { loading, error, data, reload } = useAsync(fetchAllEntries, []);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    applyPreviewSeo('All articles', SITE_NAME);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = data || [];
  const counts = useMemo(() => {
    const c = { all: rows.length };
    for (const s of STATUSES) c[s.value] = rows.filter((r) => normalizeStatus(r.status) === s.value).length;
    return c;
  }, [rows]);
  const shown = filter === 'all' ? rows : rows.filter((r) => normalizeStatus(r.status) === filter);

  return (
    <>
      <div className="preview-bar">
        <div className="blog-container preview-bar-inner">
          <span className="preview-label">Preview</span>
          <span>All articles, including those that are not public.</span>
          <span className="spacer" />
          <button type="button" className="link-button" onClick={lock}>Lock</button>
        </div>
      </div>
      <div className="blog-container preview-list">
        <h1>Articles</h1>
        <div className="chips" role="group" aria-label="Filter by status">
          {[{ value: 'all', label: 'All' }, ...STATUSES].map((s) => (
            <button
              key={s.value}
              type="button"
              className={`chip${filter === s.value ? ' active' : ''}`}
              aria-pressed={filter === s.value}
              onClick={() => setFilter(s.value)}
            >
              {s.label} ({counts[s.value] ?? 0})
            </button>
          ))}
        </div>

        {loading && <StatusMessage title="Loading articles…" />}
        {error && <LoadError error={error} onRetry={reload} technical />}
        {!loading && !error && shown.length === 0 && <StatusMessage title="No articles here" />}

        {shown.length > 0 && (
          <div className="content-table-wrap">
            <table className="content-table">
              <thead>
                <tr>
                  <th scope="col">Title</th>
                  <th scope="col">Status</th>
                  <th scope="col">Type</th>
                  <th scope="col">Imported</th>
                  <th scope="col">Link</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Title">
                      <Link to={`/preview/${row.slug}`}><strong>{row.title}</strong></Link>
                      {row.valid === false && <span className="badge badge-archived"> Invalid</span>}
                    </td>
                    <td data-label="Status"><StatusBadge status={row.status} /></td>
                    <td data-label="Type">{typeInfo(row.type).label}</td>
                    <td data-label="Imported">{formatDate(row.importedAt) || '—'}</td>
                    <td data-label="Link"><CopyLink slug={row.slug} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
